<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { doc } from 'firebase/firestore'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { useAuthStore } from '../../../app/store/index.js'
import { useCartStore } from '../../../app/store/cart.js'
import { useCatalogStore } from '../../../app/store/catalog.js'
import {
  subscribeLevelsConfig,
  computeLevelFromPoints,
  ensureUserXpFields,
} from '../../../core/services/levels.js'
import { updateUserProfile } from '../../../core/services/users.js'
import { useStorage } from '../../../core/composables/useStorage.js'
import { useToast } from '@core/composables/useToast.js'
import { ROUTES } from '../../../core/config/constants.js'
import ImageUpload from '../../components/common/ImageUpload.vue'
import ClientSelectionModal from '../../components/catalog/ClientSelectionModal.vue'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'
import { updateDocument } from '@core/firebase/firestore.js'
import { recordMarketingConsent } from '@core/services/clients.js'
import { formatDateTime } from '@core/utils/formatting.js'
import {
  hasMarketingConsent,
  hasMarketingAnswer,
  hasAcceptedSiteTerms,
  buildSiteTermsPayload,
} from '@core/utils/consent.js'
import { CONSENT_SOURCE } from '@core/config/consentConstants.js'

// ============================================
// State
// ============================================
const router = useRouter()
const authStore = useAuthStore()

// Check if user is an agent (agents don't see "my orders")
const { isAgent, isAdmin } = storeToRefs(authStore)
const isStaff = computed(() => authStore.userRole === 'agent' || authStore.userRole === 'admin')

// Navigate to My Orders page
function goToOrders() {
  router.push({ name: ROUTES.MY_ORDERS })
}

// ============================================
// Active client (sub-clients switcher)
// ============================================
const cartStore = useCartStore()
const catalogStore = useCatalogStore()
const toast = useToast()

const showClientSwitcher = ref(false)

const subClientsArr = computed(() => {
  const arr = authStore.user?.profile?.sub_clients
  return Array.isArray(arr) ? arr : []
})

const hasSubClients = computed(
  () => authStore.userRole === 'client' && subClientsArr.value.length > 0
)

const activeClientMakat = computed(() => authStore.user?.profile?.client_makat || null)
const activeClientName = computed(() => authStore.user?.profile?.client_name || '')
const mainMakat = computed(() => authStore.mainClientMakat)
const isMainActive = computed(
  () =>
    activeClientMakat.value &&
    mainMakat.value &&
    String(activeClientMakat.value) === String(mainMakat.value)
)

const switcherCandidates = computed(() => {
  const p = authStore.user?.profile
  if (!p) return []
  const list = []
  if (mainMakat.value) {
    // Main is the original client_makat. If the active one has diverged we
    // still want the main shown — pull its name from sub_clients if needed,
    // but the modal re-fetches ClientsV2 docs anyway.
    list.push({ makat: String(mainMakat.value), name: '' })
  }
  for (const sc of subClientsArr.value) {
    if (sc?.makat) list.push({ makat: String(sc.makat), name: String(sc.name || '') })
  }
  const seen = new Set()
  return list.filter((c) => {
    if (seen.has(c.makat)) return false
    seen.add(c.makat)
    return true
  })
})

function openClientSwitcher() {
  showClientSwitcher.value = true
}

async function onSwitchClient(client) {
  const newMakat = String(client.makat)
  if (newMakat === String(activeClientMakat.value || '')) {
    showClientSwitcher.value = false
    return
  }

  // Mid-session switch: warn if cart has items.
  if (cartStore.linesArray.length > 0) {
    const confirmed = window.confirm('מעבר ללקוח אחר ינקה את העגלה הנוכחית. להמשיך?')
    if (!confirmed) {
      showClientSwitcher.value = false
      return
    }
    cartStore.resetOnLogout() // clears in-memory cart without auth check
    try {
      await cartStore.clearSavedCartFromFirestore()
    } catch (err) {
      console.warn('[ProfileView] Failed to clear saved cart:', err)
    }
  }

  await authStore.setActiveClient(newMakat, client.name)
  catalogStore.reset() // mark catalog stale → next /app/catalog visit re-bootstraps
  showClientSwitcher.value = false
  toast.success('הלקוח הוחלף בהצלחה')
}

// Firestore profile doc from Users/{uid}
const profileDoc = ref(null)
const profileLoading = ref(true)
const profileError = ref(null)

// Linked client doc from ClientsV2/{client_makat}
const clientDoc = ref(null)

// ============================================
// Consents (client role only) — writes go to ClientsV2/{makat}, see consent.js
// ============================================
const isClientRole = computed(() => authStore.userRole === 'client')
const consentSaving = ref(false)
const marketingOn = computed(() => hasMarketingConsent(clientDoc.value))
const marketingAnswered = computed(() => hasMarketingAnswer(clientDoc.value))
const termsAccepted = computed(() => hasAcceptedSiteTerms(clientDoc.value))
const marketingHint = computed(() => {
  if (!marketingAnswered.value) return 'טרם בחרתם — ניתן להפעיל כאן'
  return marketingOn.value
    ? 'אתם מקבלים עדכונים במייל ובווטסאפ'
    : 'לא נשלחים אליכם עדכונים שיווקיים'
})
const termsHint = computed(() =>
  termsAcceptedAt.value ? `אושר · ${termsAcceptedAt.value}` : 'אושר'
)
const termsAcceptedAt = computed(() => {
  const at = clientDoc.value?.siteTerms?.acceptedAt
  return at ? formatDateTime(at) : ''
})

async function setMarketingConsent(consent) {
  if (consentSaving.value || !clientDoc.value?.id) return
  consentSaving.value = true
  try {
    const marketing = await recordMarketingConsent({
      clientId: clientDoc.value.id,
      consent,
      uid: authStore.userId,
      source: CONSENT_SOURCE.APP,
      previousMarketing: clientDoc.value.marketing || null,
    })
    if (authStore.user?.client) {
      authStore.user.client.marketing = { ...marketing, answeredAt: new Date() }
      authStore.user.client.tos = consent
      authStore.user.client.tos_answered = true
    }
    toast.success(consent ? 'נרשמת לקבלת מבצעים ועדכונים' : 'הוסרת מרשימת הדיוור')
  } catch (err) {
    console.error('[Profile] marketing consent update failed', err)
    toast.error('שגיאה בעדכון הדיוור')
  } finally {
    consentSaving.value = false
  }
}

async function acceptSiteTerms() {
  if (consentSaving.value || !clientDoc.value?.id) return
  consentSaving.value = true
  try {
    const siteTerms = buildSiteTermsPayload({ uid: authStore.userId, source: CONSENT_SOURCE.APP })
    await updateDocument('ClientsV2', clientDoc.value.id, { siteTerms })
    if (authStore.user?.client) {
      authStore.user.client.siteTerms = { ...siteTerms, acceptedAt: new Date() }
    }
    toast.success('אישור התקנון נרשם')
  } catch (err) {
    console.error('[Profile] site terms update failed', err)
    toast.error('שגיאה ברישום אישור התקנון')
  } finally {
    consentSaving.value = false
  }
}
const clientLoading = ref(false)
const clientError = ref(null)

// Levels config from Config/Levels
const levelsConfig = ref(null)
const levelsConfigLoading = ref(true)

const { subscribe, track } = useFirestoreSubscription()

// Track individual unsubs for watch re-subscription
let unsubProfile = null
let unsubClient = null

// Avatar editing state
const isEditingAvatar = ref(false)
const newAvatarUrl = ref('')
const savingAvatar = ref(false)
const avatarError = ref(null)
const { deleteImageByUrl } = useStorage()

// ============================================
// Computed: Auth data from store
// ============================================
const authData = computed(() => authStore.user?.authData || null)
const userId = computed(() => authStore.userId)

// Avatar: from Firestore profile only (managed by our storage)
const avatarUrl = computed(() => {
  return profileDoc.value?.photoURL || null
})

// Display name with fallbacks
const displayName = computed(() => {
  if (profileDoc.value?.name) return profileDoc.value.name
  if (authData.value?.displayName) return authData.value.displayName
  if (authData.value?.email) return authData.value.email
  return 'משתמש'
})

// Initials for avatar fallback
const initials = computed(() => {
  const name = displayName.value || ''
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

// Subtitle: status + client name (status only for admins)
const subtitle = computed(() => {
  const parts = []
  if (isAdmin.value && profileDoc.value?.status) parts.push(profileDoc.value.status)
  if (profileDoc.value?.client_name) parts.push(profileDoc.value.client_name)
  return parts.join(' | ')
})

// ============================================
// Computed: Level Info
// ============================================
const userPoints = computed(() => {
  return profileDoc.value?.xp_points || 0
})

const userLevelNumber = computed(() => {
  return profileDoc.value?.xp_level || 1
})

const levelInfo = computed(() => {
  return computeLevelFromPoints(userPoints.value, levelsConfig.value)
})

const progressPercent = computed(() => {
  return Math.round(levelInfo.value.progressRatio * 100)
})

// ============================================
// Helpers: Format timestamps
// ============================================
function formatTimestamp(ts) {
  if (!ts) return '—'

  let date

  // Handle Firestore Timestamp
  if (ts && typeof ts.toDate === 'function') {
    date = ts.toDate()
  }
  // Handle string date
  else if (typeof ts === 'string') {
    date = new Date(ts)
  }
  // Handle Date object
  else if (ts instanceof Date) {
    date = ts
  }
  // Handle milliseconds
  else if (typeof ts === 'number') {
    date = new Date(ts)
  } else {
    return '—'
  }

  if (isNaN(date.getTime())) return '—'

  return date.toLocaleString('he-IL', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatBoolean(val) {
  if (val === true) return 'כן'
  if (val === false) return 'לא'
  return '—'
}

// ============================================
// Avatar Editing
// ============================================
function startEditingAvatar() {
  // Only pre-populate with Firestore photoURL (which we manage), not Auth photoURL
  newAvatarUrl.value = profileDoc.value?.photoURL || ''
  avatarError.value = null
  isEditingAvatar.value = true
}

function cancelEditingAvatar() {
  isEditingAvatar.value = false
  newAvatarUrl.value = ''
  avatarError.value = null
}

async function saveAvatar() {
  if (!userId.value) return

  savingAvatar.value = true
  avatarError.value = null

  try {
    // Delete old image from storage if exists and is different from new one
    const oldUrl = profileDoc.value?.photoURL
    if (oldUrl && oldUrl !== newAvatarUrl.value) {
      try {
        await deleteImageByUrl(oldUrl)
      } catch (err) {
        // Ignore delete errors (file might not exist)
        console.warn('Could not delete old avatar:', err)
      }
    }

    const newPhotoURL = newAvatarUrl.value || null

    // Update Firestore with new photoURL (managed by our storage only)
    await updateUserProfile(userId.value, { photoURL: newPhotoURL })

    isEditingAvatar.value = false
    newAvatarUrl.value = ''
  } catch (err) {
    console.error('Failed to save avatar:', err)
    avatarError.value = 'שגיאה בשמירת התמונה'
  } finally {
    savingAvatar.value = false
  }
}

// ============================================
// Subscribe to Users/{uid}
// ============================================
function subscribeToProfile(uid) {
  if (!uid) {
    profileLoading.value = false
    return
  }

  profileLoading.value = true
  profileError.value = null

  const db = getFirebaseDb()
  const docRef = doc(db, 'Users', uid)

  unsubProfile = subscribe(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        profileDoc.value = { id: snapshot.id, ...snapshot.data() }
      } else {
        profileDoc.value = null
      }
      profileLoading.value = false
    },
    (error) => {
      console.error('Profile subscription error:', error)
      profileError.value = error.message || 'שגיאה בטעינת פרופיל'
      profileLoading.value = false
    }
  )
}

// ============================================
// Subscribe to ClientsV2/{client_makat}
// ============================================
function subscribeToClient(clientMakat) {
  // Clear previous subscription
  if (unsubClient) {
    unsubClient()
    unsubClient = null
  }

  if (!clientMakat) {
    clientDoc.value = null
    clientLoading.value = false
    return
  }

  clientLoading.value = true
  clientError.value = null

  const db = getFirebaseDb()
  const docRef = doc(db, 'ClientsV2', clientMakat)

  unsubClient = subscribe(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        clientDoc.value = { id: snapshot.id, ...snapshot.data() }
      } else {
        clientDoc.value = null
      }
      clientLoading.value = false
    },
    (error) => {
      console.error('Client subscription error:', error)
      clientError.value = error.message || 'שגיאה בטעינת לקוח'
      clientLoading.value = false
    }
  )
}

// ============================================
// Lifecycle
// ============================================
onMounted(async () => {
  // Subscribe to levels config
  track(
    subscribeLevelsConfig((config, err) => {
      if (err) {
        console.error('Levels config error:', err)
      } else {
        levelsConfig.value = config
      }
      levelsConfigLoading.value = false
    })
  )

  if (userId.value) {
    subscribeToProfile(userId.value)

    // Ensure user has XP fields initialized
    try {
      await ensureUserXpFields(userId.value)
    } catch (err) {
      console.error('Failed to ensure XP fields:', err)
    }
  }
})

// (Re)load the linked client whenever the session's active client changes.
// Watching authStore.user.profile.client_makat (in-memory) instead of the
// Firestore-mirrored profileDoc.client_makat so sub-client switches —
// which never touch Firestore — still refresh the "פרטי לקוח" card.
watch(
  () => authStore.user?.profile?.client_makat,
  (newMakat) => {
    subscribeToClient(newMakat)
  },
  { immediate: true }
)

// Watch for uid changes (edge case: user switches)
watch(
  () => userId.value,
  (newUid) => {
    if (unsubProfile) unsubProfile()
    if (unsubClient) unsubClient()
    clientDoc.value = null
    subscribeToProfile(newUid)
  }
)
</script>

<template>
  <div class="profile-page">
    <!-- Header -->
    <header class="profile-header">
      <div class="profile-avatar-wrapper">
        <!-- Display mode -->
        <template v-if="!isEditingAvatar">
          <img v-if="avatarUrl" :src="avatarUrl" :alt="displayName" class="profile-avatar-img" />
          <div v-else class="profile-avatar-placeholder">
            <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#283548" />
                  <stop offset="50%" stop-color="#334155" />
                  <stop offset="100%" stop-color="#64748b" />
                </linearGradient>
                <linearGradient id="shineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stop-color="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <circle cx="40" cy="40" r="40" fill="url(#avatarGradient)" />
              <circle cx="40" cy="40" r="38" fill="url(#shineGradient)" opacity="0.5" />
              <circle cx="40" cy="32" r="12" fill="rgba(255,255,255,0.9)" />
              <path
                d="M40 48c-11 0-20 6-20 14v2c0 1 0.5 2 2 3 4 2.5 11 4 18 4s14-1.5 18-4c1.5-1 2-2 2-3v-2c0-8-9-14-20-14z"
                fill="rgba(255,255,255,0.9)"
              />
              <circle
                cx="40"
                cy="40"
                r="38"
                stroke="rgba(255,255,255,0.2)"
                stroke-width="2"
                fill="none"
              />
            </svg>
          </div>
          <button class="avatar-edit-btn" @click="startEditingAvatar" title="עריכת תמונה">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
              />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        </template>

        <!-- Edit mode -->
        <div v-else class="avatar-edit-container">
          <ImageUpload v-model="newAvatarUrl" folder="user-avatars" :file-name="userId" label="" />
          <div class="avatar-edit-actions">
            <button class="btn-cancel" @click="cancelEditingAvatar" :disabled="savingAvatar">
              ביטול
            </button>
            <button class="btn-save" @click="saveAvatar" :disabled="savingAvatar">
              <span v-if="savingAvatar" class="btn-spinner"></span>
              <span v-else>שמור</span>
            </button>
          </div>
          <p v-if="avatarError" class="avatar-error">{{ avatarError }}</p>
        </div>
      </div>
      <div class="profile-header-info">
        <h1 class="profile-title">{{ displayName }}</h1>
        <p v-if="subtitle" class="profile-subtitle">{{ subtitle }}</p>
      </div>
    </header>

    <!-- Error Banner -->
    <div v-if="profileError" class="error-banner">
      {{ profileError }}
    </div>

    <!-- Loading State -->
    <div v-if="profileLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span>טוען פרופיל...</span>
    </div>

    <!-- Level Section Card -->
    <section v-if="!profileLoading && !isStaff" class="level-card">
      <div class="level-card-content">
        <!-- Level Badge -->
        <div class="level-badge-wrapper">
          <div class="level-badge-container">
            <span class="level-number-sticker">{{ levelInfo.level }}</span>
            <div class="level-badge" :class="`level-${levelInfo.level}`">
              <svg
                class="level-star"
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="currentColor"
              >
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                />
              </svg>
            </div>
          </div>
          <div class="level-name">{{ levelInfo.currentLevelObj?.name || 'מתחיל' }}</div>
        </div>

        <!-- Points & Progress -->
        <div class="level-progress-wrapper">
          <div class="level-points">
            <span class="points-value">{{ userPoints }}</span>
            <span class="points-label">נק׳</span>
          </div>

          <div class="progress-section">
            <div class="progress-bar-container">
              <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>

            <div class="progress-info">
              <template v-if="levelInfo.isMaxLevel">
                <span class="progress-text max-level">רמה מקסימלית</span>
              </template>
              <template v-else>
                <span class="progress-text">
                  עוד <strong>{{ levelInfo.pointsToNext }}</strong> נק׳ לרמה הבאה
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Active Client (only for client users with sub-clients) -->
    <section v-if="!profileLoading && hasSubClients" class="active-client-section">
      <div class="active-client-card">
        <div class="active-client-info">
          <div class="active-client-label">לקוח פעיל</div>
          <div class="active-client-name">
            <svg
              v-if="isMainActive"
              class="active-client-star"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="currentColor"
              aria-label="לקוח ראשי"
            >
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              />
            </svg>
            <span>{{ activeClientName || 'ללא שם' }}</span>
          </div>
          <div class="active-client-makat">מק"ט {{ activeClientMakat || '—' }}</div>
        </div>
        <button class="btn-switch-client" @click="openClientSwitcher">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />
          </svg>
          <span>החלף לקוח</span>
        </button>
      </div>
    </section>

    <!-- Quick Actions (for non-agent users) -->
    <section v-if="!profileLoading && !isAgent" class="quick-actions">
      <button class="orders-btn" @click="goToOrders">
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <span>ההזמנות שלי</span>
        <svg
          class="arrow-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
    </section>

    <!-- Content Cards -->
    <div v-if="!profileLoading" class="profile-cards">
      <!-- Card 1: Firestore Profile -->
      <section class="profile-card">
        <h2 class="card-title">{{ isAdmin ? 'פרטי משתמש (Firestore)' : 'פרטי משתמש' }}</h2>
        <div class="card-content">
          <div v-if="profileDoc" class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">שם מלא</span>
              <span class="kv-value">{{ profileDoc.name || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">שם פרטי</span>
              <span class="kv-value">{{ profileDoc.first_name || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">שם משפחה</span>
              <span class="kv-value">{{ profileDoc.last_name || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">אימייל</span>
              <span class="kv-value">{{ profileDoc.email || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">טלפון</span>
              <span class="kv-value">{{ profileDoc.phone || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">מק"ט לקוח</span>
              <span class="kv-value">{{ profileDoc.client_makat || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">שם לקוח</span>
              <span class="kv-value">{{ profileDoc.client_name || '—' }}</span>
            </div>
            <template v-if="isAdmin">
              <div class="kv-row">
                <span class="kv-label">סטטוס</span>
                <span class="kv-value">{{ profileDoc.status || '—' }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">עדכון סטטוס</span>
                <span class="kv-value">{{ formatTimestamp(profileDoc.status_updated_at) }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">תאריך הצטרפות</span>
                <span class="kv-value">{{ formatTimestamp(profileDoc.date_joined) }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">עדכון אחרון</span>
                <span class="kv-value">{{ formatTimestamp(profileDoc.last_update) }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">פעילות אחרונה</span>
                <span class="kv-value">{{ formatTimestamp(profileDoc.last_active_at) }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">פלטפורמה אחרונה</span>
                <span class="kv-value">{{ profileDoc.last_active_platform || '—' }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">מקור אחרון</span>
                <span class="kv-value">{{ profileDoc.last_active_source || '—' }}</span>
              </div>
            </template>
          </div>
          <div v-else class="empty-state">
            <span>אין נתוני פרופיל</span>
          </div>
        </div>
      </section>

      <!-- Card 2: Firebase Auth (admin only) -->
      <section v-if="isAdmin" class="profile-card">
        <h2 class="card-title">Firebase Auth</h2>
        <div class="card-content">
          <div v-if="authData" class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">UID</span>
              <span class="kv-value kv-mono">{{ authData.uid || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">אימייל</span>
              <span class="kv-value">{{ authData.email || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">שם תצוגה</span>
              <span class="kv-value">{{ authData.displayName || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">טלפון</span>
              <span class="kv-value">{{ authData.phoneNumber || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">תמונה</span>
              <span class="kv-value">{{ authData.photoURL ? 'קיימת' : '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">אימייל מאומת</span>
              <span class="kv-value">{{ formatBoolean(authData.emailVerified) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">תאריך יצירה</span>
              <span class="kv-value">{{ formatTimestamp(authData.metadata?.creationTime) }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">כניסה אחרונה</span>
              <span class="kv-value">{{ formatTimestamp(authData.metadata?.lastSignInTime) }}</span>
            </div>
          </div>
          <div v-else class="empty-state">
            <span>אין נתוני אימות</span>
          </div>
        </div>
      </section>

      <!-- Card 3: Linked Client (ClientsV2) -->
      <section class="profile-card">
        <h2 class="card-title">{{ isAdmin ? 'חשבון לקוח (ClientsV2)' : 'חשבון לקוח' }}</h2>
        <div class="card-content">
          <!-- Loading -->
          <div v-if="clientLoading" class="loading-inline">
            <div class="loading-spinner-sm"></div>
            <span>טוען לקוח...</span>
          </div>

          <!-- Error -->
          <div v-else-if="clientError" class="error-inline">
            {{ clientError }}
          </div>

          <!-- No client linked -->
          <div v-else-if="!profileDoc?.client_makat" class="empty-state">
            <span>לא משויך לחשבון לקוח</span>
          </div>

          <!-- Client not found -->
          <div v-else-if="!clientDoc" class="empty-state">
            <span>אין נתונים / לא נמצא לקוח</span>
          </div>

          <!-- Client data -->
          <div v-else class="kv-grid">
            <div class="kv-row">
              <span class="kv-label">מק"ט</span>
              <span class="kv-value kv-mono">{{ clientDoc.makat || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">שם</span>
              <span class="kv-value">{{ clientDoc.name || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">טלפון</span>
              <span class="kv-value">{{ clientDoc.phone || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">עיר</span>
              <span class="kv-value">{{ clientDoc.city || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">כתובת</span>
              <span class="kv-value">{{ clientDoc.address || '—' }}</span>
            </div>
            <div class="kv-row">
              <span class="kv-label">ע.מ / ח.פ</span>
              <span class="kv-value">{{ clientDoc.vat || '—' }}</span>
            </div>
            <template v-if="isAdmin">
              <div class="kv-row">
                <span class="kv-label">סטטוס</span>
                <span class="kv-value">{{ clientDoc.status || '—' }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">אמצעי תשלום</span>
                <span class="kv-value">{{ clientDoc.payment || '—' }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">מחירון</span>
                <span class="kv-value">{{ clientDoc.price_list_id || '—' }}</span>
              </div>
              <div class="kv-row">
                <span class="kv-label">סוכן</span>
                <span class="kv-value">{{ clientDoc.agent || '—' }}</span>
              </div>
            </template>
          </div>
        </div>
      </section>

      <!-- Consents (client users) -->
      <section v-if="isClientRole && clientDoc" class="profile-card">
        <h2 class="card-title">דיוור ותקנון</h2>
        <div class="card-content consent-content">
          <div class="consent-row">
            <div class="consent-text">
              <span class="consent-label">מבצעים, הנחות ותוכן שיווקי</span>
              <span class="consent-hint">{{ marketingHint }}</span>
            </div>
            <button
              type="button"
              class="consent-switch"
              :class="{ on: marketingOn }"
              role="switch"
              :aria-checked="marketingOn ? 'true' : 'false'"
              :disabled="consentSaving"
              @click="setMarketingConsent(!marketingOn)"
            >
              <span class="consent-switch-knob"></span>
            </button>
          </div>

          <div class="consent-row">
            <div class="consent-text">
              <span class="consent-label">תקנון האתר</span>
              <span class="consent-hint">
                <span>{{ termsAccepted ? termsHint : 'טרם אושר' }}</span>
                <span class="consent-links">
                  <RouterLink :to="{ name: ROUTES.POLICY }" target="_blank">תקנון</RouterLink>
                  ·
                  <RouterLink :to="{ name: ROUTES.PRIVACY }" target="_blank">
                    מדיניות פרטיות
                  </RouterLink>
                </span>
              </span>
            </div>
            <span v-if="termsAccepted" class="consent-badge">מאושר</span>
            <button
              v-else
              type="button"
              class="consent-accept-btn"
              :disabled="consentSaving"
              @click="acceptSiteTerms"
            >
              קראתי ואני מאשר/ת
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Sub-Client Switcher Modal -->
    <ClientSelectionModal
      :open="showClientSwitcher"
      :main-makat="mainMakat"
      :candidates="switcherCandidates"
      @selected="onSwitchClient"
    />
  </div>
</template>

<style scoped>
/* Consents card */
.consent-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.consent-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.consent-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.consent-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.consent-hint {
  font-size: 12px;
  color: var(--text-muted);
}

.consent-links {
  display: inline-block;
  margin-inline-start: var(--spacing-xs);
}

.consent-links a {
  color: var(--color-info);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.consent-switch {
  position: relative;
  flex-shrink: 0;
  width: 46px;
  height: 26px;
  border-radius: var(--border-radius-full);
  border: none;
  background: var(--color-gray-300);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.consent-switch.on {
  background: var(--color-success);
}

.consent-switch:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.consent-switch-knob {
  position: absolute;
  top: 3px;
  inset-inline-start: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--color-white);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
  transition: transform var(--transition-fast);
}

.consent-switch.on .consent-switch-knob {
  transform: translateX(-20px);
}

.consent-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: var(--border-radius-full);
  background: var(--color-success-light);
  color: var(--color-success);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.consent-accept-btn {
  flex-shrink: 0;
  padding: 6px 12px;
  border: none;
  border-radius: var(--border-radius);
  background: var(--color-primary);
  color: var(--text-inverse);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.consent-accept-btn:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.consent-accept-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============================================
   Page Container
   ============================================ */
.profile-page {
  padding: var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
}

/* ============================================
   Profile Header
   ============================================ */
.profile-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.profile-avatar-wrapper {
  flex-shrink: 0;
  position: relative;
}

.profile-avatar-img {
  width: 80px;
  height: 80px;
  border-radius: var(--border-radius-full);
  object-fit: cover;
}

.profile-avatar-placeholder {
  width: 80px;
  height: 80px;
  border-radius: var(--border-radius-full);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(40, 53, 72, 0.3);
}

.profile-avatar-placeholder svg {
  width: 100%;
  height: 100%;
  display: block;
}

.profile-header-info {
  flex: 1;
  min-width: 0;
}

.profile-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.profile-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   Avatar Edit
   ============================================ */
.avatar-edit-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  background: var(--color-primary);
  border: 2px solid var(--bg-primary);
  border-radius: var(--border-radius-full);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  box-shadow: var(--shadow-md);
}

.avatar-edit-btn:hover {
  background: var(--color-primary-dark, #1e293b);
  transform: scale(1.1);
}

.avatar-edit-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  width: 160px;
}

.avatar-edit-container :deep(.image-upload) {
  width: 100%;
}

.avatar-edit-container :deep(.upload-area) {
  aspect-ratio: 1;
  max-height: 160px;
  border-radius: var(--border-radius-full);
}

.avatar-edit-container :deep(.image-preview img) {
  border-radius: var(--border-radius-full);
}

.avatar-edit-actions {
  display: flex;
  gap: var(--spacing-xs);
  justify-content: center;
}

.avatar-edit-actions button {
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  min-width: 60px;
}

.btn-cancel {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.btn-cancel:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.btn-save {
  background: var(--color-primary);
  border: 1px solid var(--color-primary);
  color: white;
}

.btn-save:hover:not(:disabled) {
  background: var(--color-primary-dark, #1e293b);
}

.btn-save:disabled,
.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.avatar-error {
  font-size: var(--font-size-xs);
  color: var(--color-error);
  text-align: center;
  margin: 0;
}

/* ============================================
   Error Banner
   ============================================ */
.error-banner {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
  text-align: center;
}

/* ============================================
   Loading States
   ============================================ */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--text-muted);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-inline {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--text-muted);
  padding: var(--spacing-md);
}

.loading-spinner-sm {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-inline {
  color: var(--color-error);
  padding: var(--spacing-md);
}

/* ============================================
   Cards Grid
   ============================================ */
.profile-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--spacing-lg);
}

.profile-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.card-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.card-content {
  padding: var(--spacing-md);
}

/* ============================================
   Key/Value Grid
   ============================================ */
.kv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0;
}

.kv-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
}

.kv-row:last-child {
  border-bottom: none;
}

.kv-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.kv-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  text-align: left;
  word-break: break-word;
}

.kv-mono {
  font-family: monospace;
  font-size: 12px;
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
}

/* ============================================
   Empty State
   ============================================ */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* ============================================
   Level Card
   ============================================ */
.level-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  margin-bottom: var(--spacing-lg);
  overflow: hidden;
  border: 1px solid rgba(40, 53, 72, 0.2);
}

.level-card-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  padding: var(--spacing-lg);
}

.level-badge-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.level-badge-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.level-number-sticker {
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 2;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: white;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: var(--border-radius-full);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
  border: 2px solid var(--bg-primary);
}

.level-badge {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #283548 0%, #334155 100%);
  border-radius: var(--border-radius-full);
  box-shadow: 0 4px 12px rgba(40, 53, 72, 0.3);
}

.level-badge.level-1 {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

.level-badge.level-2 {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.level-badge.level-3 {
  background: linear-gradient(135deg, #334155 0%, #64748b 100%);
  box-shadow: 0 4px 12px rgba(51, 65, 85, 0.3);
}

.level-badge.level-4 {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.level-star {
  color: rgba(255, 255, 255, 0.6);
}

.level-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.level-progress-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.level-points {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-xs);
}

.points-value {
  font-size: 32px;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.points-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.progress-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-full);
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #283548 0%, #334155 100%);
  border-radius: var(--border-radius-full);
  transition: width 0.5s ease-out;
}

.progress-info {
  display: flex;
  justify-content: flex-start;
}

.progress-text {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.progress-text strong {
  color: var(--color-primary);
}

.progress-text.max-level {
  color: #f59e0b;
  font-weight: var(--font-weight-medium);
}

.level-footer {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.level-footer svg {
  flex-shrink: 0;
}

/* ============================================
   Active Client (sub-client switcher)
   ============================================ */
.active-client-section {
  margin-bottom: var(--spacing-lg);
}

.active-client-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.active-client-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.active-client-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
}

.active-client-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.active-client-star {
  color: var(--color-warning);
  flex-shrink: 0;
}

.active-client-makat {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.btn-switch-client {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.btn-switch-client:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-switch-client:active {
  transform: translateY(0);
}

/* ============================================
   Quick Actions
   ============================================ */
.quick-actions {
  margin-bottom: var(--spacing-lg);
}

.orders-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-family);
  text-align: right;
}

.orders-btn:hover {
  background: var(--bg-secondary);
  border-color: var(--color-primary);
  box-shadow: var(--shadow-md);
}

.orders-btn svg:first-child {
  color: var(--color-primary);
  flex-shrink: 0;
}

.orders-btn span {
  flex: 1;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.orders-btn .arrow-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 1023px) {
  .profile-page {
    padding-bottom: calc(var(--mobile-footer-height) + var(--spacing-md));
  }
}

@media (max-width: 768px) {
  .profile-page {
    padding: var(--spacing-md);
    padding-bottom: calc(var(--mobile-footer-height) + var(--spacing-md));
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
    padding: var(--spacing-md);
  }

  .profile-avatar-img,
  .profile-avatar-placeholder {
    width: 64px;
    height: 64px;
  }

  .avatar-edit-btn {
    width: 28px;
    height: 28px;
  }

  .avatar-edit-btn svg {
    width: 14px;
    height: 14px;
  }

  .avatar-edit-container {
    width: 120px;
  }

  .avatar-edit-container :deep(.upload-area) {
    max-height: 120px;
  }

  .profile-title {
    font-size: var(--font-size-lg);
  }

  .profile-cards {
    grid-template-columns: 1fr;
  }

  .kv-grid {
    grid-template-columns: 1fr;
  }

  .kv-row {
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: var(--spacing-sm);
  }

  .kv-value {
    text-align: right;
  }

  /* Level card responsive */
  .level-card-content {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-lg);
  }

  .level-progress-wrapper {
    width: 100%;
    align-items: center;
  }

  .level-points {
    justify-content: center;
  }

  .progress-info {
    justify-content: center;
  }

  .level-footer {
    justify-content: center;
    text-align: center;
  }
}
</style>
